import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { CloseButton, Input, InputProps, Popover, PopoverProps } from '@mantine/core';
import { assignRef, useMergedRef } from '@mantine/hooks';
import { MdSchedule } from 'react-icons/md';
import { POPOVER_Z_INDEX } from '../../lib/zIndex';
import { usePortalTarget } from '../../provider/UiContext';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import { useSyncedMask } from '../../hooks/useSyncedMask';
import MantineDescribedBy from '../../theme/MantineDescribedBy';
import TimeColumns from './TimeColumns';

export interface IMaskedTimeInputProps {
    /** Время HH:mm (или HH:mm:ss при withSeconds) либо '' */
    value?: string | null;
    onChange?: (next: string) => void;
    withSeconds?: boolean;
    invalid?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    clearable?: boolean;
    placeholder?: string;
    name?: string;
    /** id поля ввода — для `<label htmlFor>` */
    id?: string;
    variant?: InputProps['variant'];
    className?: string;
    leftSection?: ReactNode;
    /** Свои настройки поповера выбора времени; хост портала по умолчанию — `portalTarget` из `UiProvider`. */
    popoverProps?: Partial<PopoverProps>;
    'data-testid'?: string;
    'aria-label'?: string;
    /** id подписи поля; в `FormGroup` по умолчанию — её подпись */
    'aria-labelledby'?: string;
    /** id описания; в `FormGroup` к нему добавляются сообщение под полем и пояснение группы */
    'aria-describedby'?: string;
    /** Ссылка на поле ввода — фокус поля формы (`setFocus`, ошибка валидации) */
    ref?: React.Ref<HTMLInputElement>;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    w?: InputProps['w'];
}

// Маска посимвольно: первая цифра часов 0-2, первая цифра минут/секунд 0-5.
// Часы 24-29 (первая 2 + вторая 4-9) отсекаем уже на валидации при коммите.
const buildMask = (withSeconds: boolean): RegExp[] | (RegExp | string)[] =>
    withSeconds ? [/[0-2]/, /\d/, ':', /[0-5]/, /\d/, ':', /[0-5]/, /\d/] : [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

/**
 * Масочное поле времени HH:mm[:ss] через @mantine/hooks useMask (посимвольный
 * Backspace, авто-разделители) + колоночный выбор (часы/минуты/[секунды]) в Popover
 * в одном визуальном языке с календарём.
 *
 * Замена сегментированного Mantine TimePicker: единая маска чинит и ручной ввод,
 * и баг, когда Backspace по сегменту обнулял всё значение. useMask владеет
 * <input>, поэтому value↔текст синхронизируем императивно (pushText). На
 * readonly/disabled маску не вешаем.
 */
const MaskedTimeInput: React.FC<IMaskedTimeInputProps> = ({
    value,
    onChange,
    withSeconds = false,
    invalid,
    disabled,
    readOnly,
    clearable = true,
    placeholder,
    name,
    id,
    variant,
    className,
    leftSection,
    popoverProps,
    'data-testid': dataTestid,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ref,
    onBlur,
    onFocus,
    onKeyDown,
    w = '100%',
}) => {
    const editable = !readOnly && !disabled;
    const [opened, setOpened] = useState(false);
    const portalTarget = usePortalTarget();

    const inputRef = useRef<HTMLInputElement | null>(null);
    const fieldA11y = useFieldA11y({
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
    });
    const lastEmittedRef = useRef<string | undefined>(undefined);
    const valueRef = useRef<string>(value ?? '');
    valueRef.current = value ?? '';

    const emit = (next: string) => {
        if (next === valueRef.current || next === lastEmittedRef.current) return;
        lastEmittedRef.current = next;
        onChange?.(next);
    };

    const mask = useSyncedMask({
        mask: buildMask(withSeconds),
        invalid,
        // Коммит на каждом изменении с заполненной маской, НЕ через onComplete:
        // тот стреляет только на переходе «не заполнено → заполнено», а при
        // перепечатке цифр поверх уже заполненного значения маска остаётся
        // заполненной — ввод не коммитился и откатывался на blur.
        onChangeRaw: (raw, masked) => {
            if (raw === '') {
                emit('');
                return;
            }
            if (raw.length < (withSeconds ? 6 : 4)) return;
            const hh = Number(masked.slice(0, 2));
            if (hh > 23) return; // 24-29 — невалидные часы
            emit(masked);
        },
    });

    // Текущий <input> (с маской или только для чтения). Внешний ref получает его эффектом, а не в merged-ref:
    // новая функция ref у потребителя пере-прикрепила бы ref маски и сбросила каретку (см. useSyncedMask).
    const fieldRef = useRef<HTMLInputElement | null>(null);
    const mergedRef = useMergedRef(mask.ref, inputRef, fieldRef);
    useEffect(() => {
        if (!ref) return undefined;
        assignRef(ref, fieldRef.current);
        return () => {
            assignRef(ref, null);
        };
    }, [ref, editable]);

    // Текст поля мимо ввода: без события input и без onChange.
    const pushText = (time: string) => {
        const el = inputRef.current;
        if (el && el.value !== time) mask.write(time);
    };

    // После readOnly/disabled поле с маской — новый пустой <input>: текст в него пишем и без смены value.
    useEffect(() => {
        const v = value ?? '';
        const isOwnEcho = v === lastEmittedRef.current;
        lastEmittedRef.current = undefined;
        if (!isOwnEcho) pushText(v);
    }, [value, editable]);

    // Выбор части времени из столбцов: коммитим, но НЕ закрываем — пользователь
    // может дальше выбрать минуты/секунды.
    const handleColumnsChange = (next: string) => {
        emit(next);
        pushText(next);
    };

    const handleClear = () => {
        emit('');
        pushText('');
        setOpened(false);
    };

    const showClear = clearable && editable && !!(value ?? '');
    const clockIcon = leftSection ?? <MdSchedule size={16} aria-hidden />;

    const sharedInputProps = {
        // Алиасим под старый Mantine TimePicker, чтобы внешние стили на
        // .mantine-TimePicker-{wrapper,input,section} продолжали работать.
        classNames: {
            wrapper: 'mantine-TimePicker-wrapper',
            input: 'mantine-TimePicker-input',
            section: 'mantine-TimePicker-section',
        },
        variant,
        name,
        id,
        placeholder: placeholder ?? (withSeconds ? 'чч:мм:сс' : 'чч:мм'),
        leftSection: clockIcon,
        'data-error': invalid || undefined,
        // aria-invalid ставит сам Mantine (withAria) по error и затирает переданный; вид ошибки — от data-error поля
        // (у обёртки Mantine data-error перекрасил бы иконку)
        error: invalid || undefined,
        withErrorStyles: false,
        'data-testid': dataTestid,
        'aria-label': ariaLabel,
        'aria-labelledby': fieldA11y['aria-labelledby'],
        w,
        className,
        autoComplete: 'off' as const,
    };

    if (!editable) {
        return (
            <MantineDescribedBy ids={fieldA11y['aria-describedby']}>
                <Input
                    {...sharedInputProps}
                    ref={fieldRef}
                    readOnly={readOnly}
                    disabled={disabled}
                    value={value ?? ''}
                    onChange={() => undefined}
                />
            </MantineDescribedBy>
        );
    }

    return (
        <Popover
            opened={opened}
            trapFocus={false}
            withRoles={false}
            position="bottom-start"
            shadow="md"
            zIndex={POPOVER_Z_INDEX}
            portalProps={portalTarget ? { target: portalTarget } : undefined}
            {...popoverProps}
        >
            <Popover.Target>
                <div style={{ width: typeof w === 'number' ? w : '100%' }}>
                    <MantineDescribedBy ids={fieldA11y['aria-describedby']}>
                        <Input
                            {...sharedInputProps}
                            ref={mergedRef}
                            rightSection={
                                showClear ? (
                                    <CloseButton
                                        size="sm"
                                        variant="transparent"
                                        aria-label="Очистить"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={handleClear}
                                    />
                                ) : undefined
                            }
                            rightSectionPointerEvents={showClear ? 'all' : 'none'}
                            onFocus={(e) => {
                                setOpened(true);
                                onFocus?.(e);
                            }}
                            onClick={() => setOpened(true)}
                            onBlur={(e) => {
                                // Клики по списку времени не блюрят (onMouseDown preventDefault
                                // на dropdown) → blur = ушли наружу, закрываем + fixOnBlur.
                                setOpened(false);
                                pushText(valueRef.current);
                                onBlur?.(e);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setOpened(false);
                                    e.currentTarget.blur();
                                } else if (e.key === 'Escape') {
                                    // Первый Escape закрывает выбор времени, а не окно или панель вокруг поля (стек окон
                                    // пропускает погашенный Escape).
                                    if (opened) e.preventDefault();
                                    setOpened(false);
                                }
                                onKeyDown?.(e);
                            }}
                        />
                    </MantineDescribedBy>
                </div>
            </Popover.Target>
            <Popover.Dropdown onMouseDown={(e) => e.preventDefault()} p="xs">
                <TimeColumns value={value ?? ''} withSeconds={withSeconds} onChange={handleColumnsChange} />
            </Popover.Dropdown>
        </Popover>
    );
};

export default MaskedTimeInput;
